import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MAX_MESSAGE_LENGTH } from '../domain/conversations';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import RecipientPicker from '../components/messaging/RecipientPicker';
import { ActionError, EmptyState, LoadingState, StateForError } from '../components/PageStates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Users } from 'lucide-react';
import { ErrorText } from '../components/ErrorText';

/**
 * `/conversations/new` — start a conversation: one person opens our direct
 * conversation (the one we already have, if any); two or more start a group
 * with an optional name. The first message goes with it.
 */
export default function NewMessagePage() {
  const { conversations } = useRepositories();
  const navigate = useNavigate();
  const recipients = useLoad(() => conversations.recipients(), 'recipients');
  const [selected, setSelected] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const body = text.trim();
  const tooLong = body.length > MAX_MESSAGE_LENGTH;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const c =
        selected.length === 1
          ? await conversations.openDirect(selected[0])
          : await conversations.createGroup(selected, groupName.trim() || null);
      await conversations.send(c.id, {
        body,
        photoIds: [],
        idempotencyKey: crypto.randomUUID(),
      });
      navigate(`/conversations/${c.id}`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  let content;
  if (recipients.status === 'loading') content = <LoadingState label="Loading people" />;
  else if (recipients.status === 'error')
    content = (
      <StateForError error={recipients.error} onRetry={recipients.reload} what="Messages" />
    );
  else if (recipients.data.length === 0)
    content = (
      <EmptyState
        icon={<Users className="size-8" />}
        title="Nobody to message yet"
        description="You can message your connections and people in your private collections."
      />
    );
  else
    content = (
      <div className="space-y-6">
        <RecipientPicker people={recipients.data} selected={selected} onChange={setSelected} />
        {selected.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="group-name">Group name (optional)</Label>
            <Input
              id="group-name"
              value={groupName}
              maxLength={100}
              onChange={(e) => setGroupName(e.target.value)}
              className="rounded-2xl"
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="first-message">Message</Label>
          <Textarea
            id="first-message"
            value={text}
            aria-invalid={tooLong}
            onChange={(e) => setText(e.target.value)}
            className="min-h-24 rounded-2xl"
          />
          {tooLong && (
            <ErrorText role="alert" className="text-xs">
              Messages can be up to {MAX_MESSAGE_LENGTH.toLocaleString('en-US')} characters.
            </ErrorText>
          )}
        </div>
        {error !== null && <ActionError error={error} />}
        <Button
          className="rounded-full"
          disabled={busy || selected.length === 0 || !body || tooLong}
          onClick={start}
        >
          Send
        </Button>
      </div>
    );

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link
          to="/conversations"
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="size-4" />
          Messages
        </Link>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">New message</h1>
      </div>
      {content}
    </div>
  );
}
